import type { FetchPageServiceParameters } from "~/core/composables/useFetchPage";
import type { GenericParameters } from "~/core/models/GenericParameters.type";
import type { PaginatedList } from "~/core/models/PaginatedList.interface";
import RestService from "~/core/services/rest.service";
import type { Fleet } from "~/modules/fleet/models/Fleet.interface";
import type { FleetCreationPayload } from "~/modules/fleet/models/FleetCreationPayload.interface";
import type { FleetUpdatePayload } from "~/modules/fleet/models/FleetUpdatePayload.interface";
import organizationService from "~/modules/organization/services/organization.service";

export class FleetService extends RestService {
  async getOrganizationFleets({
    organizationCode,
    page,
    pageSize,
    searchParams,
    sort,
    params,
  }: FetchPageServiceParameters): Promise<PaginatedList<Fleet>> {
    return this.$apiV2.get(this.getOrganizationFleetsPath(organizationCode), {
      params: {
        page,
        pageSize,
        filter: searchParams,
        sort,
        ...(params ? Object.fromEntries(params) : {}),
      },
    });
  }

  async getOrganizationFleet({
    organizationCode,
    uid,
  }: GenericParameters & { uid: string }): Promise<Fleet> {
    return this.$apiV2.get(
      `${this.getOrganizationFleetsPath(organizationCode)}/${uid}`,
    );
  }

  async createOrganizationFleet({
    organizationCode,
    fleet,
  }: GenericParameters & { fleet: FleetCreationPayload }): Promise<Fleet> {
    return this.$apiV2.post(
      `${this.getOrganizationFleetsPath(organizationCode)}`,
      fleet,
    );
  }

  async updateOrganizationFleet({
    organizationCode,
    uid,
    fleetUpdatePayload,
  }: GenericParameters & {
    uid: string;
    fleetUpdatePayload: FleetUpdatePayload;
  }): Promise<void> {
    return this.$apiV2.patch(
      `${this.getOrganizationFleetsPath(organizationCode)}/${uid}`,
      fleetUpdatePayload,
    );
  }

  async deleteOrganizationFleet({
    organizationCode,
    uid,
  }: GenericParameters & { uid: string }): Promise<void> {
    return this.$apiV2.delete(
      `${this.getOrganizationFleetsPath(organizationCode)}/${uid}`,
    );
  }

  async bulkDeleteOrganizationFleets({
    organizationCode,
    fleetUIDs,
  }: GenericParameters & { fleetUIDs: string[] }): Promise<void> {
    return this.$apiV2.delete(
      `${this.getOrganizationFleetsPath(organizationCode)}/bulk`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        data: { fleetUIDs },
      },
    );
  }

  private getOrganizationFleetsPath(organizationCode?: number) {
    return `${organizationService.getOrganizationPath(organizationCode)}/fleets`;
  }
}

export default new FleetService();
